import { useOutletContext, Link } from "react-router-dom";
import { ChevronRight, MapPin } from "lucide-react";
import SEO from "../components/SEO";
import SearchWidget from "../components/SearchWidget";
import DestinationCard from "../components/DestinationCard";
import Reveal from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import { t } from "../i18n";
import { hubPages, hubPath, staticPath, siteUrl } from "../data";

export default function HubPage({ hub }) {
  const { lang } = useOutletContext();
  const i = t[lang];
  const c = hub[lang];
  const list = hubPages(hub);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: c.h1,
        description: c.metaDesc,
        url: siteUrl(hubPath(lang, hub)),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: i.common.home, item: siteUrl(`/${lang}`) },
          {
            "@type": "ListItem",
            position: 2,
            name: i.common.breadcrumbDestinations,
            item: siteUrl(staticPath(lang, "carRental")),
          },
          { "@type": "ListItem", position: 3, name: c.h1 },
        ],
      },
      ...(c.faqs
        ? [
            {
              "@type": "FAQPage",
              mainEntity: c.faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ]
        : []),
    ],
  };

  return (
    <>
      <SEO
        lang={lang}
        title={c.title}
        description={c.metaDesc}
        pathEn={hubPath("en", hub)}
        pathIt={hubPath("it", hub)}
        jsonLd={jsonLd}
      />
      <section className="container-site pt-6 md:pt-10" data-testid={`hub-${hub.id}`}>
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <li>
              <Link to={`/${lang}`} data-testid="hub-breadcrumb-home" className="transition-colors duration-200 hover:text-petrol">
                {i.common.home}
              </Link>
            </li>
            <li className="flex items-center gap-1.5">
              <ChevronRight className="h-3 w-3 opacity-50" aria-hidden="true" />
              <Link to={staticPath(lang, "carRental")} data-testid="hub-breadcrumb-destinations" className="transition-colors duration-200 hover:text-petrol">
                {i.common.breadcrumbDestinations}
              </Link>
            </li>
            <li className="flex items-center gap-1.5">
              <ChevronRight className="h-3 w-3 opacity-50" aria-hidden="true" />
              <span aria-current="page" className="text-foreground">{c.h1}</span>
            </li>
          </ol>
        </nav>

        <div className="mt-6 grid gap-8 lg:grid-cols-2 lg:items-start lg:gap-14">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-petrol/10 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-petrol">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              {i.nav.destinations}
            </p>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tighter text-foreground sm:text-4xl lg:text-5xl">
              {c.h1}
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground/80 md:text-base">{c.intro}</p>
          </div>
          <div id="search">
            <div className="rounded-3xl bg-white p-4 shadow-2xl ring-1 ring-black/5 sm:p-6" data-testid="hub-widget-card">
              <SearchWidget lang={lang} channel={hub.chan} />
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">{i.common.affiliateNote}</p>
          </div>
        </div>
      </section>

      <section className="container-site py-12 md:py-16" data-testid="hub-listing">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((p, idx) => (
            <Reveal key={p.id} delay={(idx % 4) * 0.05}>
              <DestinationCard page={p} lang={lang} />
            </Reveal>
          ))}
        </div>
      </section>

      {(c.extra || c.faqs) && (
        <section className="bg-white py-12 md:py-16" data-testid="hub-content">
          <div className="container-site">
            {c.extra && (
              <div className="grid gap-8 md:grid-cols-3 md:gap-10">
                {c.extra.map((x, idx) => (
                  <Reveal key={x.h} delay={idx * 0.06}>
                    <div className="border-t-2 border-foreground pt-4">
                      <h2 className="font-display text-lg font-bold tracking-tight">{x.h}</h2>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{x.p}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
            {c.faqs && (
              <div className="mt-12 grid gap-10 lg:grid-cols-5">
                <Reveal className="lg:col-span-2">
                  <h2 className="font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">
                    {i.common.faqTitle}
                  </h2>
                </Reveal>
                <Reveal className="lg:col-span-3" delay={0.08}>
                  <FaqAccordion faqs={c.faqs} testIdPrefix={`hub-${hub.id}-faq`} />
                </Reveal>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}
